/**
 * Meal Plan Actions Hook
 * Production-ready hook with optimistic updates, undo, and conflict handling
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import {
  addRecipeToMealPlan,
  removeRecipeFromMealPlan,
  updateMealPlanSlot,
} from 'wasp/client/operations';
import { mealPlanKeys } from '../queries/mealPlanKeys';
import { MEAL_PLAN_FEATURES } from '../config/features';
import { MEAL_PLAN_CONFIG } from '../config/mealPlan';
import type {
  AddToPlanPayload,
  MealPlanEntry,
  OptimisticMealPlanUpdate,
  ConflictStatus,
  RemoveFromSlotPayload,
  UpdateSlotPayload,
} from '../types/mealPlan';

/**
 * Undo queue for reverting meal plan changes
 * Stores pending undo operations with 7s timeout
 */
interface UndoQueueItem {
  toastId: string | number;
  rollback: () => void;
  timestamp: number;
}

const undoQueue: UndoQueueItem[] = [];

/**
 * Add recipe to meal plan with optimistic updates
 */
export function useMealPlanActions(userId: string) {
  const queryClient = useQueryClient();

  /**
   * Add Recipe Mutation
   * Features: Optimistic updates, conflict detection, undo support
   */
  const addRecipeMutation = useMutation({
    mutationFn: async (payload: AddToPlanPayload) => {
      // Generate idempotency key if not provided
      const idempotencyKey = payload.idempotencyKey || uuidv4();

      // Call server action
      return addRecipeToMealPlan({
        recipeId: payload.recipeId,
        dayIndex: payload.dayIndex,
        mealType: payload.mealType,
        servings: payload.servingsTotal,
        source: payload.source as 'external' | 'custom' | undefined,
        idempotencyKey,
      });
    },

    // Optimistic update: immediately update cache before server responds
    onMutate: async (payload: AddToPlanPayload) => {
      if (!MEAL_PLAN_FEATURES.OPTIMISTIC_UPDATES) {
        return; // Skip optimistic updates if feature is disabled
      }

      const { recipeId, dayIndex, mealType, servingsTotal, weekId } = payload;

      if (!weekId) {
        console.warn('[useMealPlanActions] Cannot apply optimistic update without weekId');
        return;
      }

      // Cancel outgoing queries to prevent race conditions
      await queryClient.cancelQueries(
        mealPlanKeys.week(userId, weekId)
      );

      // Snapshot the current state for rollback
      const previousData = queryClient.getQueryData<MealPlanEntry[]>(
        mealPlanKeys.week(userId, weekId)
      );

      // Optimistically update the cache
      const tempId = `temp-${uuidv4()}`;
      const optimisticEntry: MealPlanEntry = {
        id: tempId,
        mealPlanId: 'pending',
        weekId,
        dayIndex,
        slot: mealType,
        slotSequence: 0,
        recipeId,
        servingsTotal,
        peopleCount: payload.peopleCount,
        sortOrder: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      queryClient.setQueryData<MealPlanEntry[]>(
        mealPlanKeys.week(userId, weekId),
        (old = []) => {
          // Remove existing entry in this slot (if replacing)
          const filtered = old.filter(
            (entry) =>
              !(entry.dayIndex === dayIndex && entry.slot === mealType)
          );
          return [...filtered, optimisticEntry];
        }
      );

      // Return context for rollback
      return { previousData, weekId, tempId };
    },

    // Rollback on error
    onError: (error, payload, context) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to add recipe';

      // Rollback optimistic update
      if (context?.previousData && context.weekId) {
        queryClient.setQueryData(
          mealPlanKeys.week(userId, context.weekId),
          context.previousData
        );
      }

      toast.error('Failed to add recipe', {
        description: errorMessage,
        duration: 5000,
      });
    },

    // Success: replace temp entry with real data
    onSuccess: (data, payload, context) => {
      const weekId = payload.weekId || context?.weekId;

      if (!weekId) {
        console.warn('[useMealPlanActions] Cannot invalidate without weekId');
        return;
      }

      // Invalidate queries to refetch fresh data
      queryClient.invalidateQueries(
        mealPlanKeys.week(userId, weekId)
      );

      // Show success toast with undo option
      if (MEAL_PLAN_FEATURES.UNDO_ENABLED) {
        const rollback = () => {
          // TODO: Implement undo by calling remove mutation
          console.log('[useMealPlanActions] Undo triggered');
        };

        const toastId = toast.success('Recipe added to meal plan', {
          description: 'Added successfully',
          duration: MEAL_PLAN_CONFIG.UNDO_TIMEOUT_MS,
          action: {
            label: 'Undo',
            onClick: () => {
              rollback();
              toast.dismiss(toastId);
            },
          },
        });

        // Add to undo queue
        undoQueue.push({
          toastId,
          rollback,
          timestamp: Date.now(),
        });

        // Auto-cleanup after timeout
        setTimeout(() => {
          const index = undoQueue.findIndex((item) => item.toastId === toastId);
          if (index !== -1) {
            undoQueue.splice(index, 1);
          }
        }, MEAL_PLAN_CONFIG.UNDO_TIMEOUT_MS);
      } else {
        toast.success('Recipe added to meal plan', {
          description: 'Added successfully',
        });
      }
    },
  });

  /**
   * Check for conflicts before adding
   * Returns conflict status if slot is already occupied
   */
  const checkConflict = async (
    weekId: string,
    dayIndex: number,
    mealType: string
  ): Promise<ConflictStatus> => {
    if (!MEAL_PLAN_FEATURES.CONFLICT_DETECTION) {
      return { hasConflict: false };
    }

    const entries = queryClient.getQueryData<MealPlanEntry[]>(
      mealPlanKeys.week(userId, weekId)
    );

    const existingEntry = entries?.find(
      (entry) => entry.dayIndex === dayIndex && entry.slot === mealType
    );

    if (existingEntry?.recipeId) {
      // TODO: Fetch recipe details for conflict dialog
      return {
        hasConflict: true,
        existingEntry: {
          recipeId: existingEntry.recipeId,
          recipeTitle: 'Existing Recipe', // Placeholder
          servings: existingEntry.servingsTotal || 0,
        },
      };
    }

    return { hasConflict: false };
  };

  /**
   * Remove Recipe Mutation
   * Features: Optimistic updates, undo support
   */
  const removeRecipeMutation = useMutation({
    mutationFn: async (payload: RemoveFromSlotPayload) => {
      return removeRecipeFromMealPlan({
        mealPlanId: payload.slotId.split('-')[0], // Extract mealPlanId from slotId
        dayIndex: payload.dayIndex,
        mealType: payload.mealType,
      });
    },

    onMutate: async (payload: RemoveFromSlotPayload) => {
      if (!MEAL_PLAN_FEATURES.OPTIMISTIC_UPDATES) {
        return;
      }

      const { weekId, dayIndex, mealType } = payload;

      await queryClient.cancelQueries(
        mealPlanKeys.week(userId, weekId)
      );

      const previousData = queryClient.getQueryData<MealPlanEntry[]>(
        mealPlanKeys.week(userId, weekId)
      );

      // Optimistically remove the entry
      queryClient.setQueryData<MealPlanEntry[]>(
        mealPlanKeys.week(userId, weekId),
        (old = []) => old.filter(
          (entry) =>
            !(entry.dayIndex === dayIndex && entry.slot === mealType)
        )
      );

      return { previousData, weekId };
    },

    onError: (error, payload, context) => {
      if (context?.previousData && context.weekId) {
        queryClient.setQueryData(
          mealPlanKeys.week(userId, context.weekId),
          context.previousData
        );
      }

      toast.error('Failed to remove recipe', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },

    onSuccess: (data, payload, context) => {
      const weekId = payload.weekId;

      queryClient.invalidateQueries(
        mealPlanKeys.week(userId, weekId)
      );

      toast.success('Recipe removed', {
        description: data.message,
      });
    },
  });

  /**
   * Update Slot Mutation
   * Features: Optimistic updates for servings changes
   */
  const updateSlotMutation = useMutation({
    mutationFn: async (payload: UpdateSlotPayload & { mealPlanId: string; dayIndex: number; mealType: string }) => {
      return updateMealPlanSlot({
        mealPlanId: payload.mealPlanId,
        dayIndex: payload.dayIndex,
        mealType: payload.mealType,
        servingsTotal: payload.servingsTotal,
        peopleCount: payload.peopleCount,
      });
    },

    onSuccess: (data) => {
      toast.success('Updated successfully', {
        description: 'Changes saved',
      });
    },

    onError: (error) => {
      toast.error('Update failed', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  return {
    /**
     * Add recipe to meal plan
     */
    addRecipe: addRecipeMutation.mutate,
    addRecipeAsync: addRecipeMutation.mutateAsync,

    /**
     * Remove recipe from meal plan
     */
    removeRecipe: removeRecipeMutation.mutate,
    removeRecipeAsync: removeRecipeMutation.mutateAsync,

    /**
     * Update meal plan slot (servings, people count)
     */
    updateSlot: updateSlotMutation.mutate,
    updateSlotAsync: updateSlotMutation.mutateAsync,

    /**
     * Check for slot conflicts
     */
    checkConflict,

    /**
     * Mutation states
     */
    isAdding: (addRecipeMutation as any).isLoading ?? false,
    isRemoving: (removeRecipeMutation as any).isLoading ?? false,
    isUpdating: (updateSlotMutation as any).isLoading ?? false,
    addError: addRecipeMutation.error,
    removeError: removeRecipeMutation.error,
    updateError: updateSlotMutation.error,
  };
}
